import React, { useState, useRef, useMemo, useEffect } from "react";
import {
  Modal,
  Button,
  TextInput,
  NumberInput,
  Select,
  Checkbox,
  Group,
  Stack,
  SimpleGrid,
  Box,
  LoadingOverlay,
  Alert,
  Text,
  ActionIcon,
  Image,
  Tabs,
  SegmentedControl,
  Center,
  Loader,
  UnstyledButton
} from "@mantine/core";
import { useForm } from "@mantine/form";
import RichTextEditor from "./RichTextEditor";
import {
  IconAlertCircle,
  IconArrowLeft,
  IconArrowRight,
  IconArrowsMove,
  IconCheck,
  IconHash,
  IconInfoCircle,
  IconLanguage,
  IconPackage,
  IconPhoto,
  IconPlus,
  IconScale,
  IconEye,
  IconUpload,
  IconX,
} from "@tabler/icons-react";
import apiService from "../services/api";
import ProductPreview from "./ProductPreview";
import { useLanguages, DEFAULT_LANG_CODES } from "./LanguageTabs";
import {
  generateParentCode,
  generateVariantSku,
  getWeightUnitOptions,
} from "../utils/skuPreview";

const API_BASE_URL =
  process.env.REACT_APP_API_URL || "http://localhost:3001/api";
const MAX_FILE_SIZE_MB = 2;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

function resolvePreviewUrl(idOrUrl) {
  if (!idOrUrl) return null;
  if (idOrUrl.startsWith("http")) return idOrUrl;
  return `${API_BASE_URL}/files/${idOrUrl}`;
}

function extractFileId(idOrUrl) {
  if (!idOrUrl) return null;
  const match = idOrUrl.match(
    /\/files\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})$/i
  );
  if (match) return match[1];
  const uuidPattern =
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  if (uuidPattern.test(idOrUrl)) return idOrUrl;
  return null;
}

function parseImages(raw) {
  let arr = [];
  if (Array.isArray(raw)) arr = raw;
  else if (typeof raw === "string") {
    try {
      const parsed = JSON.parse(raw);
      arr = Array.isArray(parsed) ? parsed : [];
    } catch { arr = []; }
  }
  return arr.map(entry => {
    if (entry && typeof entry === "object" && entry.id) {
      return { id: entry.id, lang: entry.lang || null };
    }
    if (typeof entry === "string") {
      return { id: entry, lang: null };
    }
    return null;
  }).filter(Boolean);
}

function serializeImages(imageEntries) {
  return imageEntries.map(e => {
    if (e.lang) return { id: e.id, lang: e.lang };
    return e.id;
  });
}

export default function AddEditProductModal({
  product,
  mode,
  parentCode,
  onClose,
  onSaved,
}) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [activeTab, setActiveTab] = useState("details");

  const isEditParent = mode === "edit-parent";
  const isEditVariant = mode === "edit-variant";
  const isAddParent = mode === "add-parent";

  // Edit-parent: inline variant management
  const [variants, setVariants] = useState(product?.variants || []);
  const [showAddVariant, setShowAddVariant] = useState(false);

  const handleSetDefault = async (sku) => {
    try {
      await apiService.updateVariant(sku, { is_default: true });
      setVariants((prev) =>
        prev.map((v) => ({ ...v, is_default: v.sku === sku }))
      );
    } catch (e) {
      setError(e.message || "Failed to set default variant.");
    }
  };

  const [parentImages, setParentImages] = useState(
    parseImages(product?.images).map(img => ({ ...img, saved: true }))
  );
  const parentFileRef = useRef();

  const [variantImages, setVariantImages] = useState(
    parseImages(product?.images).map(img => ({ ...img, saved: true }))
  );
  const variantFileRef = useRef();
  const parentImagesRef = useRef(parentImages);
  const variantImagesRef = useRef(variantImages);
  parentImagesRef.current = parentImages;
  variantImagesRef.current = variantImages;
  const initialParentImagesRef = useRef(parentImages);
  const initialVariantImagesRef = useRef(variantImages);

  const activeLanguages = useLanguages();

  const [categories, setCategories] = useState([]);
  useEffect(() => {
    apiService.getAdminProductCategories().then(setCategories).catch(() => {});
  }, []);

  useEffect(() => {
    return () => {
      parentImagesRef.current.filter(i => !i.saved).forEach(i => URL.revokeObjectURL(i.preview));
      variantImagesRef.current.filter(i => !i.saved).forEach(i => URL.revokeObjectURL(i.preview));
    };
  }, []);

  // Build translatable field keys from active languages
  const translatableFields = ["name", "description"];
  const langCodes = activeLanguages.length > 0 ? activeLanguages.map(l => l.code) : DEFAULT_LANG_CODES;

  // Build translation map from product data
  const getTranslation = (field, langCode) => {
    const tr = product?.translations?.find?.(t => t.language_code === langCode);
    return tr?.[field] || '';
  };

  const initialVals = {
    category: product?.category ?? "",
    is_bulk_only: !!product?.is_bulk_only,
    is_variable_weight: !!product?.is_variable_weight,
    is_hidden: !!product?.is_hidden,
    article_number: product?.article_number ?? "",
    vat_rate: product?.vat_rate ?? 2.6,
    price: product?.price != null ? parseFloat(product.price) : "",
    weight_value: product?.weight_value ?? "",
    weight_unit: product?.weight_unit ?? "",
    is_default: !!product?.is_default,
    variant_is_hidden: !!product?.is_hidden,
    business_only: !!product?.business_only,
    can_preorder: product?.can_preorder == null || Number(product?.can_preorder) !== 0,
    sort_order: product?.sort_order ?? 0,
  };
  for (const langCode of langCodes) {
    for (const field of translatableFields) {
      initialVals[`${field}_${langCode}`] = getTranslation(field, langCode);
    }
  }

  // Build validation rules dynamically
  const validateRules = {
    category: (v) => ((isAddParent || isEditParent) && !v ? "Category is required" : null),
    price: (value) => (isAddParent || isEditVariant) && !initialVals.is_bulk_only && (value === "" || value === null) ? "Price is required" : null,
    weight_value: (value) => (isAddParent || isEditVariant) && !initialVals.is_bulk_only && !value ? "Weight is required" : null,
    weight_unit: (value) => (isAddParent || isEditVariant) && !initialVals.is_bulk_only && !value ? "Unit is required" : null,
  };
  const requiredTranslatableFields = ["name"];
  for (const langCode of langCodes) {
    for (const field of requiredTranslatableFields) {
      const key = `${field}_${langCode}`;
      const label = field.replace(/_/g, ' ');
      validateRules[key] = (v) => (isAddParent || isEditParent) && !v ? `${label.charAt(0).toUpperCase() + label.slice(1)} is required` : null;
    }
  }

  const form = useForm({
    initialValues: initialVals,
    validate: validateRules,
  });

  const previewParentCode = useMemo(
    () => form.values.category ? generateParentCode(form.values.category, form.values[`name_${langCodes[0]}`]) : '',
    [form.values.category, langCodes[0], form.values[`name_${langCodes[0]}`]]
  );

  const effectiveParentCode = useMemo(() => {
    if (isEditVariant) return parentCode;
    return previewParentCode;
  }, [isEditVariant, parentCode, previewParentCode]);

  const previewVariantSku = useMemo(
    () =>
      generateVariantSku(
        effectiveParentCode,
        form.values.weight_value,
        form.values.weight_unit
      ),
    [effectiveParentCode, form.values.weight_value, form.values.weight_unit]
  );

  const handleImageSelect = (e, target) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;

    const oversized = files.filter((f) => f.size > MAX_FILE_SIZE_BYTES);
    if (oversized.length > 0) {
      setError(
        `File(s) too large: ${oversized.map((f) => f.name).join(", ")}. Max is ${MAX_FILE_SIZE_MB}MB.`
      );
      return;
    }

    setError("");
    const setter = target === "parent" ? setParentImages : setVariantImages;
    const newEntries = files.map((file) => ({
      file,
      preview: URL.createObjectURL(file),
      lang: null,
      saved: false,
    }));
    setter((prev) => [...prev, ...newEntries]);
    const ref = target === "parent" ? parentFileRef : variantFileRef;
    if (ref.current) ref.current.value = "";
  };

  const handleRemoveImage = (index, target) => {
    const setter = target === "parent" ? setParentImages : setVariantImages;
    setter((prev) => {
      const img = prev[index];
      if (img && !img.saved && img.preview) URL.revokeObjectURL(img.preview);
      return prev.filter((_, i) => i !== index);
    });
  };

  const handleImageLangChange = (index, lang, target) => {
    const setter = target === "parent" ? setParentImages : setVariantImages;
    setter((prev) => prev.map((img, i) => i === index ? { ...img, lang } : img));
  };

  const handleAddExistingImage = (target, imageEntry) => {
    const setter = target === "parent" ? setParentImages : setVariantImages;
    setter((prev) => [...prev, { ...imageEntry, saved: true }]);
  };

  const handleReorderImages = (fromIdx, toIdx, target) => {
    if (fromIdx === toIdx) return;
    const setter = target === "parent" ? setParentImages : setVariantImages;
    setter((prev) => {
      const next = [...prev];
      const [moved] = next.splice(fromIdx, 1);
      next.splice(toIdx, 0, moved);
      return next;
    });
  };

  const uploadImages = async (images) => {
    const pending = images.filter(i => !i.saved);
    if (!pending.length) return [];
    const fd = new FormData();
    pending.forEach(p => fd.append("files", p.file));
    const { uploaded } = await apiService.uploadFiles(fd);
    return uploaded.map(f => f.id);
  };

  const deleteRemovedFiles = async (removedIds) => {
    for (const fileId of removedIds) {
      await apiService
        .deleteFile(fileId)
        .catch((err) =>
          console.warn(`Failed to delete file ${fileId}:`, err.message)
        );
    }
  };

  const buildParentPayload = async (values) => {
    const uploadedIds = await uploadImages(parentImages);
    let uploadIdx = 0;
    const finalImages = parentImages.map(img => {
      if (img.saved) return { id: img.id, lang: img.lang || null };
      return { id: uploadedIds[uploadIdx++], lang: img.lang || null };
    });
    const imageIds = serializeImages(finalImages);
    // Build translations array dynamically
    const translations = langCodes.map(langCode => {
      const entry = { language_code: langCode };
      for (const field of translatableFields) {
        entry[field] = values[`${field}_${langCode}`] || null;
      }
      return entry;
    });
    return {
      category: values.category,
      translations,
      is_bulk_only: values.is_bulk_only,
      is_variable_weight: !!values.is_variable_weight,
      is_hidden: values.is_hidden,
      images: imageIds,
      vat_rate: values.vat_rate,
    };
  };

  const buildVariantPayload = async (values) => {
    const uploadedIds = await uploadImages(variantImages);
    let uploadIdx = 0;
    const finalImages = variantImages.map(img => {
      if (img.saved) return { id: img.id, lang: img.lang || null };
      return { id: uploadedIds[uploadIdx++], lang: img.lang || null };
    });
    const imageIds = finalImages.length > 0 ? serializeImages(finalImages) : null;
    return {
      price: values.is_bulk_only ? null : (values.price != null ? values.price : null),
      weight_value: values.weight_value != null ? values.weight_value : null,
      weight_unit: values.weight_unit || null,
      is_default: !!values.is_default,
      is_hidden: !!values.variant_is_hidden,
      business_only: !!values.business_only,
      can_preorder: values.can_preorder == null || Number(values.can_preorder) !== 0,
      sort_order: values.sort_order != null ? values.sort_order : 0,
      article_number: values.article_number || null,
      images: imageIds && imageIds.length > 0 ? imageIds : null,
    };
  };

  const computeRemovedIds = (initialImages, currentImages) => {
    const currentIds = new Set(currentImages.filter(i => i.saved).map(i => i.id));
    return initialImages
      .filter(i => i.saved && i.id && !currentIds.has(i.id))
      .map(i => extractFileId(i.id))
      .filter(Boolean);
  };

  const handleSave = async (values) => {
    setError("");
    setLoading(true);

    try {
      if (isEditParent) {
        const parentPayload = await buildParentPayload(values);
        await apiService.updateProduct(product.product_code, parentPayload);
        await deleteRemovedFiles(computeRemovedIds(initialParentImagesRef.current, parentImages));

        if (showAddVariant && form.values.weight_value && form.values.weight_unit) {
          const variantPayload = await buildVariantPayload(values);
          await apiService.addVariant(product.product_code, variantPayload);
          await deleteRemovedFiles(computeRemovedIds(initialVariantImagesRef.current, variantImages));
        }

        onSaved();
        return;
      }

      if (isAddParent) {
        const parentPayload = await buildParentPayload(values);
        const result = await apiService.addProduct(parentPayload);
        const code = result.product_code || previewParentCode;

        const variantPayload = await buildVariantPayload(values);
        await apiService.addVariant(code, variantPayload);
        await deleteRemovedFiles(computeRemovedIds(initialVariantImagesRef.current, variantImages));

        onSaved();
        return;
      }

      if (isEditVariant) {
        const variantPayload = await buildVariantPayload(values);
        await apiService.updateVariant(product.sku, variantPayload);
        await deleteRemovedFiles(computeRemovedIds(initialVariantImagesRef.current, variantImages));

        onSaved();
        return;
      }
    } catch (e) {
      setError(e.message || "An error occurred while saving.");
    } finally {
      setLoading(false);
    }
  };

  const getTitle = () => {
    if (isEditParent) return "Edit Product";
    if (isEditVariant) return "Edit Variant";
    return "Add New Product";
  };

  const PARENT_TABS = ["details", ...langCodes, "images", "variants", "preview"];

  const TAB_FIELDS = {
    details: ["category"],
    images: [],
    variants: ["price", "weight_value", "weight_unit"],
    preview: [],
  };
  for (const langCode of langCodes) {
    TAB_FIELDS[langCode] = requiredTranslatableFields.map(f => `${f}_${langCode}`);
  }

  const handleNextTab = () => {
    const fields = TAB_FIELDS[activeTab] || [];
    let hasError = false;
    for (const field of fields) {
      const result = form.validateField(field);
      if (result.hasError) hasError = true;
    }
    if (hasError) return;

    const idx = PARENT_TABS.indexOf(activeTab);
    if (idx < PARENT_TABS.length - 1) {
      setActiveTab(PARENT_TABS[idx + 1]);
    }
  };

  const renderParentFields = () => (
    <Tabs value={activeTab} onChange={setActiveTab}>
      <Tabs.List>
        <Tabs.Tab value="details" leftSection={<IconInfoCircle size={16} />}>Details</Tabs.Tab>
        {langCodes.map(langCode => {
          const lang = activeLanguages.find(l => l.code === langCode);
          return (
            <Tabs.Tab key={langCode} value={langCode} leftSection={<IconLanguage size={16} />}>
              {lang?.native_name || langCode.toUpperCase()}
            </Tabs.Tab>
          );
        })}
        <Tabs.Tab value="images" leftSection={<IconPhoto size={16} />}>Images</Tabs.Tab>
        <Tabs.Tab value="variants" leftSection={<IconScale size={16} />}>Variants</Tabs.Tab>
        <Tabs.Tab value="preview" leftSection={<IconEye size={16} />}>Preview</Tabs.Tab>
      </Tabs.List>

      <Tabs.Panel value="details" pt="md">
        <Stack gap="md">
          <SimpleGrid cols={{ base: 1, sm: 2 }}>
            <Select
              label="Category"
              placeholder="Pick one"
              data={categories.filter(c => c.is_active !== false).map(c => ({ value: c.name, label: c.name }))}
              {...form.getInputProps("category")}
              required
            />
            <NumberInput
              label="VAT Rate (%)"
              step={0.1}
              decimalScale={2}
              {...form.getInputProps("vat_rate")}
            />
          </SimpleGrid>

          <TextInput
            label="Product Code (auto-generated)"
            value={isEditParent ? product.product_code : previewParentCode}
            readOnly
            leftSection={<IconHash size={16} />}
            description="Generated from category + English name"
            styles={{ input: { backgroundColor: "#f1f3f5", cursor: "not-allowed" } }}
          />

          <Group mt="xs">
            <Checkbox
              label="Bulk Only (Hide price)"
              {...form.getInputProps("is_bulk_only", { type: "checkbox" })}
            />
            <Checkbox
              label="Variable Weight"
              {...form.getInputProps("is_variable_weight", { type: "checkbox" })}
            />
            <Checkbox
              label="Hide from Store"
              {...form.getInputProps("is_hidden", { type: "checkbox" })}
            />
          </Group>
        </Stack>
      </Tabs.Panel>

      {langCodes.map(langCode => (
          <Tabs.Panel key={langCode} value={langCode} pt="md">
            <Stack gap="sm">
              <TextInput label="Name" {...form.getInputProps(`name_${langCode}`)} required />
              <Box>
                <Text component="label" size="sm" fw={500} mb={4} display="block">Description</Text>
                <RichTextEditor
                  value={form.values[`description_${langCode}`] || ''}
                  onChange={(val) => form.setFieldValue(`description_${langCode}`, val)}
                  placeholder="Write the product description here. Use headings, lists, and bold text to structure information (e.g. ingredients, nutritional values, etc.)."
                />
              </Box>
            </Stack>
          </Tabs.Panel>
        ))}

      <Tabs.Panel value="images" pt="md">
        <ImageGrid
          images={parentImages}
          fileRef={parentFileRef}
          onSelect={(e) => handleImageSelect(e, "parent")}
          onRemove={(i) => handleRemoveImage(i, "parent")}
          onLangChange={(i, lang) => handleImageLangChange(i, lang, "parent")}
          onAddExistingImage={(entry) => handleAddExistingImage("parent", entry)}
          onReorder={(from, to) => handleReorderImages(from, to, "parent")}
          label="Parent Images"
          hint="Common images shown for all languages"
        />
      </Tabs.Panel>

      <Tabs.Panel value="variants" pt="md">
        <Stack gap="md">
          {isEditParent && variants.map((v) => (
            <Box
              key={v.sku}
              p="sm"
              style={{
                border: "1px solid #e9ecef",
                borderRadius: 6,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Group gap="sm">
                <Text size="sm" fw={500}>
                  {v.weight_value != null
                    ? `${Number.isInteger(v.weight_value) ? v.weight_value : v.weight_value.toFixed(1)}${v.weight_unit || ""}`
                    : v.sku}
                </Text>
                {v.is_default ? (
                  <Text size="xs" c="blue" fw={600}>Default</Text>
                ) : (
                  <Button
                    variant="subtle"
                    size="compact-xs"
                    onClick={() => handleSetDefault(v.sku)}
                  >
                    Set as Default
                  </Button>
                )}
                {v.is_hidden ? <Text size="xs" c="dimmed">Hidden</Text> : null}
                {v.article_number ? <Text size="xs" c="dimmed">Art: {v.article_number}</Text> : null}
                <Text size="xs" c="dimmed">
                  CHF {v.price != null ? parseFloat(v.price).toFixed(2) : "—"}
                </Text>
                <Text size="xs" c="dimmed">
                  Stock: {v.stock_quantity ?? "—"}
                </Text>
              </Group>
              <Text size="xs" c="dimmed">{v.sku}</Text>
            </Box>
          ))}

          {isEditParent && variants.length === 0 && (
            <Text size="sm" c="dimmed" fs="italic">
              No variants yet.
            </Text>
          )}

          {isEditParent && !showAddVariant && (
            <Button
              variant="light"
              size="sm"
              leftSection={<IconPlus size={16} />}
              onClick={() => setShowAddVariant(true)}
            >
              Add Variant
            </Button>
          )}

          {isEditParent && showAddVariant && (
            <Box
              p="md"
              style={{
                border: "2px dashed #228be6",
                borderRadius: 8,
                background: "#f8f9fa",
              }}
            >
              <Group justify="space-between" mb="sm">
                <Text fw={600} size="sm">New Variant</Text>
                <Button
                  variant="subtle"
                  size="xs"
                  color="gray"
                  onClick={() => {
                    setShowAddVariant(false);
                    form.setValues({
                      price: "",
                      weight_value: "",
                      weight_unit: "",
                      is_default: false,
                      variant_is_hidden: false,
                      can_preorder: true,
                      sort_order: 0,
                    });
                  }}
                >
                  Cancel
                </Button>
              </Group>
              {renderVariantFields()}
            </Box>
          )}

          {isAddParent && renderVariantFields()}
        </Stack>
      </Tabs.Panel>

      <Tabs.Panel value="preview" pt="md">
        <ProductPreview
          formValues={form.values}
          parentImages={parentImages}
          variantImages={variantImages}
          variants={variants}
          langCodes={langCodes}
          isBulkOnly={form.values.is_bulk_only}
          isVariableWeight={form.values.is_variable_weight}
        />
      </Tabs.Panel>
    </Tabs>
  );

  const renderVariantFields = () => {
    const vatRate = Number(form.values.vat_rate) || 0;
    const netPrice = form.values.price;
    const retailGrossPrice = !form.values.is_bulk_only && vatRate > 0 && netPrice !== "" && netPrice != null
      ? Number(netPrice) * (1 + vatRate / 100)
      : null;
    return (
    <Stack gap="md">
      <TextInput
        label="Variant SKU (auto-generated)"
        value={previewVariantSku || product?.sku || ""}
        readOnly
        leftSection={<IconHash size={16} />}
        description="Generated from parent code + weight"
        styles={{
          input: { backgroundColor: "#f1f3f5", cursor: "not-allowed" },
        }}
      />

      <TextInput
        label="Article Number"
        placeholder="e.g. ART-12345"
        {...form.getInputProps("article_number")}
      />

      <SimpleGrid cols={{ base: 1, sm: 3 }}>
        <Stack gap={4}>
          <NumberInput
            label="Price excl. VAT (CHF)"
            placeholder="0.00"
            decimalScale={2}
            fixedDecimalScale
            hideControls
            {...form.getInputProps("price")}
            disabled={form.values.is_bulk_only}
          />
          {retailGrossPrice != null && (
            <Text size="xs" c="dimmed" role="status" id="net-price-preview">
              Retail price (incl. VAT): CHF {retailGrossPrice.toFixed(2)}
            </Text>
          )}
        </Stack>
        <NumberInput
          label="Sort Order"
          {...form.getInputProps("sort_order")}
        />
      </SimpleGrid>

      <Group grow align="flex-end">
        <NumberInput
          label="Weight / Volume"
          placeholder="Value"
          decimalScale={2}
          {...form.getInputProps("weight_value")}
          required={!form.values.is_bulk_only}
          disabled={form.values.is_bulk_only}
        />
        <Select
          label="Unit"
          placeholder="Unit"
          data={getWeightUnitOptions()}
          {...form.getInputProps("weight_unit")}
          required={!form.values.is_bulk_only}
          disabled={form.values.is_bulk_only}
        />
      </Group>

      <Group mt="xs">
        <Checkbox
          label="Default Variant"
          {...form.getInputProps("is_default", { type: "checkbox" })}
        />
        <Checkbox
          label="Hide Variant from Store"
          {...form.getInputProps("variant_is_hidden", { type: "checkbox" })}
        />
        <Checkbox
          label="For Business Customers Only"
          {...form.getInputProps("business_only", { type: "checkbox" })}
        />
        <Checkbox
          label="Allow Pre-Orders (orders beyond stock)"
          {...form.getInputProps("can_preorder", { type: "checkbox" })}
        />
      </Group>

      <ImageGrid
        images={variantImages}
        fileRef={variantFileRef}
        onSelect={(e) => handleImageSelect(e, "variant")}
        onRemove={(i) => handleRemoveImage(i, "variant")}
        onLangChange={(i, lang) => handleImageLangChange(i, lang, "variant")}
        onAddExistingImage={(entry) => handleAddExistingImage("variant", entry)}
        onReorder={(from, to) => handleReorderImages(from, to, "variant")}
        label="Variant Images"
        hint="Leave empty to inherit from parent. Tag images with a language to show them only for that language."
      />
    </Stack>
    );
  };

  return (
    <Modal
      opened
      onClose={onClose}
      title={
        <Group gap="xs">
          <IconPackage size={20} stroke={1.5} />
          <Text fw={700} size="lg">
            {getTitle()}
          </Text>
        </Group>
      }
      size="xl"
      centered
      radius="md"
    >
      <Box pos="relative">
        <LoadingOverlay
          visible={loading}
          zIndex={1000}
          overlayProps={{ blur: 2 }}
          transitionProps={{ duration: 200 }}
        />

        {error && (
          <Alert
            icon={<IconAlertCircle size={16} />}
            title="Error"
            color="red"
            mb="md"
            withCloseButton
            onClose={() => setError("")}
          >
            {error}
          </Alert>
        )}

        <form onSubmit={form.onSubmit(
          handleSave,
          (errors) => {
            // Find the first failing field and switch the active tab to
            // whatever owns it, so the admin lands at the failing input.
            try {
              const fieldToTab = Object.fromEntries(
                Object.entries(TAB_FIELDS).flatMap(([tab, fields]) => fields.map((f) => [f, tab]))
              );
              const firstErrorField = Object.keys(errors)[0];
              const failingTab = firstErrorField && fieldToTab[firstErrorField];
              if (failingTab && failingTab !== activeTab) {
                setActiveTab(failingTab);
              }
              // After the tab switch renders, focus the failing field so
              // keyboard users land directly on it. 200 ms gives Mantine's
              // Tabs.Panel a chance to mount before we DOM-query for the input.
              // Mantine hides inactive panels via CSS (display: none), NOT
              // the HTML `hidden` attribute, so we walk all the tabpanels
              // and find the one that's actually visible. Then we reset the
              // modal scroll containers to the top so the error Alert is
              // visible, gently scroll the failing field into view (block:
              // 'nearest' avoids overshooting), and focus the field without
              // re-scrolling.
              if (firstErrorField) {
                setTimeout(() => {
                  const panels = document.querySelectorAll('[role="tabpanel"]');
                  const visiblePanel = Array.from(panels).find(
                    (p) => getComputedStyle(p).display !== 'none'
                  );
                  if (!visiblePanel) return;

                  const el = visiblePanel.querySelector(`[name="${firstErrorField}"]`);
                  if (!el) return;

                  // Reset the modal scroll containers to the top so the
                  // error Alert remains in view above the failing field.
                  const scrollables = [
                    el.closest('.mantine-Modal-body'),
                    el.closest('.mantine-Modal-content'),
                    el.closest('[role="dialog"]'),
                    el.closest('[role="alert"]')?.parentElement,
                  ];
                  scrollables.forEach((c) => {
                    if (c && typeof c.scrollTo === 'function') {
                      c.scrollTo({ top: 0, behavior: 'instant' });
                    }
                  });

                  // Pull the failing field into view if it's still off-screen.
                  el.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });

                  // Focus without overriding our manual scroll.
                  if (typeof el.focus === 'function') {
                    el.focus({ preventScroll: true });
                  }
                }, 200);
              }
            } catch (e) {
              // Never let focus management crash a save attempt.
              console.warn('Failed to focus first invalid field:', e);
            }
          }
        )} noValidate>
          {/* Parent views: add-parent or edit-parent */}
          {(isAddParent || isEditParent) && renderParentFields()}

          {/* Variant-only view: edit-variant */}
          {isEditVariant && renderVariantFields()}

          <Group justify="flex-end" mt="xl">
            <Button
              variant="subtle"
              color="gray"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </Button>
            {isAddParent && PARENT_TABS.indexOf(activeTab) > 0 && (
              <Button
                variant="default"
                leftSection={<IconArrowLeft size={18} />}
                onClick={() => {
                  const idx = PARENT_TABS.indexOf(activeTab);
                  setActiveTab(PARENT_TABS[idx - 1]);
                }}
              >
                Back
              </Button>
            )}
            {isAddParent && activeTab !== "preview" ? (
              <Button
                type="button"
                loading={loading}
                rightSection={<IconArrowRight size={18} />}
                onClick={handleNextTab}
              >
                Next
              </Button>
            ) : (
              <Button
                type="submit"
                loading={loading}
                leftSection={<IconCheck size={18} />}
              >
                {isEditParent
                  ? "Save Changes"
                  : isEditVariant
                    ? "Update Variant"
                    : "Create Product"}
              </Button>
            )}
          </Group>
        </form>
      </Box>
    </Modal>
  );
}

function ImageGrid({
  images,
  fileRef,
  onSelect,
  onRemove,
  onLangChange,
  onAddExistingImage,
  onReorder,
  label,
  hint,
}) {
  const langOptions = [
    { value: "", label: "All" },
    { value: "en", label: "EN" },
    { value: "de", label: "DE" },
  ];

  const [mediaOpened, setMediaOpened] = useState(false);
  const [mediaFiles, setMediaFiles] = useState([]);
  const [mediaLoading, setMediaLoading] = useState(false);
  const [mediaFilter, setMediaFilter] = useState("");

  const [dragIdx, setDragIdx] = useState(null);
  const [overIdx, setOverIdx] = useState(null);

  const openMediaCenter = async () => {
    setMediaOpened(true);
    setMediaLoading(true);
    try {
      const files = await apiService.getFiles();
      setMediaFiles(files.filter(f => f.mimetype && f.mimetype.startsWith("image/")));
    } catch {
      setMediaFiles([]);
    } finally {
      setMediaLoading(false);
    }
  };

  const filteredMedia = mediaFilter
    ? mediaFiles.filter(f => f.filename.toLowerCase().includes(mediaFilter.toLowerCase()))
    : mediaFiles;

  const handlePickMedia = (file) => {
    onAddExistingImage({ id: file.id, lang: null });
    setMediaOpened(false);
  };

  const handleDragStart = (idx) => setDragIdx(idx);
  const handleDragOver = (e, idx) => { e.preventDefault(); setOverIdx(idx); };
  const handleDrop = (idx) => {
    if (dragIdx !== null && dragIdx !== idx) onReorder(dragIdx, idx);
    setDragIdx(null);
    setOverIdx(null);
  };
  const handleDragEnd = () => { setDragIdx(null); setOverIdx(null); };

  return (
    <Box>
      <Modal
        opened={mediaOpened}
        onClose={() => setMediaOpened(false)}
        title={<Text fw={700}>Media Center</Text>}
        size="xl"
        centered
      >
        <TextInput
          placeholder="Filter by filename..."
          value={mediaFilter}
          onChange={(e) => setMediaFilter(e.currentTarget.value)}
          mb="md"
        />
        {mediaLoading ? (
          <Center py="xl"><Loader /></Center>
        ) : filteredMedia.length === 0 ? (
          <Text c="dimmed" ta="center" py="xl">No images found in media center.</Text>
        ) : (
          <SimpleGrid cols={{ base: 3, sm: 4, md: 5 }} spacing="xs">
            {filteredMedia.map((file) => (
              <UnstyledButton
                key={file.id}
                onClick={() => handlePickMedia(file)}
                style={{
                  border: "2px solid var(--mantine-color-gray-3)",
                  borderRadius: 8,
                  overflow: "hidden",
                  transition: "border-color 0.15s",
                  lineHeight: 0,
                }}
                onMouseEnter={(e) => e.currentTarget.style.borderColor = "var(--mantine-color-blue-5)"}
                onMouseLeave={(e) => e.currentTarget.style.borderColor = "var(--mantine-color-gray-3)"}
              >
                <Image
                  src={resolvePreviewUrl(file.id)}
                  h={90}
                  fit="cover"
                  fallbackSrc="https://placehold.co/100x100?text=?"
                />
              </UnstyledButton>
            ))}
          </SimpleGrid>
        )}
      </Modal>

      <Text size="sm" fw={500} mb="xs">
        {label}{" "}
        {hint && (
          <Text component="span" size="xs" c="dimmed">
            ({hint})
          </Text>
        )}
      </Text>
      <Text size="xs" c="dimmed" mb="sm">
        Drag images or use arrows to reorder. First image is shown on product cards and detail pages.
        Tag images with a language to show them only to users viewing in that language. "All" means the image is shown for every language.
      </Text>
      <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="md">
        {images.map((img, i) => {
          const preview = img.saved ? resolvePreviewUrl(img.id) : img.preview;
          const isDragging = dragIdx === i;
          const isOver = overIdx === i;

          return (
            <Box
              key={img.saved ? img.id : `pending-${i}`}
              pos="relative"
              draggable
              onDragStart={() => handleDragStart(i)}
              onDragOver={(e) => handleDragOver(e, i)}
              onDrop={() => handleDrop(i)}
              onDragEnd={handleDragEnd}
              style={{
                borderRadius: 8,
                overflow: "hidden",
                border: isOver && dragIdx !== i
                  ? "2px dashed var(--mantine-color-blue-5)"
                  : img.saved ? "1px solid #dee2e6" : "2px dashed #228be6",
                opacity: isDragging ? 0.4 : 1,
                cursor: "grab",
                transition: "opacity 0.15s, border-color 0.15s",
              }}
            >
              <Image
                src={preview}
                h={100}
                fallbackSrc={img.saved ? "https://placehold.co/400x400?text=No+Image" : undefined}
              />
              <ActionIcon
                variant="filled"
                color="red"
                pos="absolute"
                top={5}
                right={5}
                size="sm"
                onClick={(e) => { e.stopPropagation(); onRemove(i); }}
              >
                <IconX size={14} />
              </ActionIcon>
              <Group
                gap={2}
                pos="absolute"
                top={5}
                left={5}
                style={{ pointerEvents: "auto" }}
              >
                <ActionIcon
                  variant="filled"
                  color="dark"
                  size="sm"
                  radius="sm"
                  style={{ cursor: "grab", opacity: 0.7 }}
                  title="Drag to reorder"
                  aria-label="Drag handle"
                >
                  <IconArrowsMove size={14} />
                </ActionIcon>
                {i > 0 && (
                  <ActionIcon
                    variant="filled"
                    color="dark"
                    size="sm"
                    radius="sm"
                    onClick={(e) => { e.stopPropagation(); onReorder(i, i - 1); }}
                    title="Move left"
                    aria-label="Move image left"
                    style={{ opacity: 0.7 }}
                  >
                    <IconArrowLeft size={14} />
                  </ActionIcon>
                )}
                {i < images.length - 1 && (
                  <ActionIcon
                    variant="filled"
                    color="dark"
                    size="sm"
                    radius="sm"
                    onClick={(e) => { e.stopPropagation(); onReorder(i, i + 1); }}
                    title="Move right"
                    aria-label="Move image right"
                    style={{ opacity: 0.7 }}
                  >
                    <IconArrowRight size={14} />
                  </ActionIcon>
                )}
              </Group>
              <SegmentedControl
                size="xs"
                fullWidth
                mt={4}
                mb={4}
                px={4}
                data={langOptions}
                value={img.lang || ""}
                onChange={(val) => onLangChange(i, val || null)}
              />
            </Box>
          );
        })}
      </SimpleGrid>
      <Group gap="xs" mt="sm">
        <Button
          variant="light"
          size="sm"
          leftSection={<IconUpload size={16} />}
          onClick={() => fileRef.current?.click()}
          style={{ flex: 1 }}
        >
          Upload New
        </Button>
        <Button
          variant="light"
          color="green"
          size="sm"
          leftSection={<IconPhoto size={16} />}
          onClick={openMediaCenter}
          style={{ flex: 1 }}
        >
          Media Center
        </Button>
      </Group>
      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        multiple
        onChange={onSelect}
        style={{ display: "none" }}
      />
    </Box>
  );
}
